import type { ReplicaDatabase } from '../db/replica-database';
import type { FileStore } from '../files/file-store';
import { ApiError } from '../http/api-error';
import type { HttpClient } from '../http/http-client';
import { NetworkError } from '../http/network-error';
import type { Rejection } from '../types/rejection';
import type { SyncState } from '../types/sync-state';
import { pullChanges } from './pull-changes';
import { pushOutbox } from './push-outbox';

const INTERVAL_MS = 30_000;
const DEBOUNCE_MS = 800;
const LAST_SYNC_KEY = 'kit.lastSyncAt';

type Listener = () => void;
type RejectionListener = (rejections: readonly Rejection[]) => void;

/**
 * Upload files, push the outbox, pull changes, download files. Runs on start, every 30 s while
 * visible, on reconnect, on returning to the foreground and soon after each local change.
 * One tab syncs at a time (Web Locks). A 401 pauses it until `resume()` (after signing in).
 */
export class SyncEngine {
  private state: SyncState = {
    status: 'idle',
    lastSyncAt: globalThis.localStorage?.getItem(LAST_SYNC_KEY) ?? null,
    lastError: null,
  };
  private readonly listeners = new Set<Listener>();
  private readonly rejectionListeners = new Set<RejectionListener>();
  private running: Promise<void> | null = null;
  private again = false;
  private paused = false;
  private debounce: ReturnType<typeof setTimeout> | undefined;
  private stopLoop: (() => void) | null = null;

  constructor(
    private readonly db: ReplicaDatabase,
    private readonly http: HttpClient,
    private readonly files: FileStore,
    private readonly isSignedIn: () => boolean,
  ) {}

  getState = (): SyncState => this.state;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  onRejections(listener: RejectionListener): () => void {
    this.rejectionListeners.add(listener);
    return () => this.rejectionListeners.delete(listener);
  }

  private setState(next: Partial<SyncState>): void {
    this.state = { ...this.state, ...next };
    if (next.lastSyncAt) globalThis.localStorage?.setItem(LAST_SYNC_KEY, next.lastSyncAt);
    for (const listener of this.listeners) listener();
  }

  private async deviceId(): Promise<string> {
    let id = await this.db.getMeta<string>('device_id');
    if (!id) {
      id = crypto.randomUUID();
      await this.db.setMeta('device_id', id);
    }
    return id;
  }

  private async run(): Promise<void> {
    if (!this.isSignedIn()) return this.setState({ status: 'signed_out' });
    if (this.paused) return this.setState({ status: 'unauthorized' });
    if (navigator.onLine === false) return this.setState({ status: 'offline' });
    this.setState({ status: 'syncing' });
    try {
      await this.files.uploadPending();
      const rejected = await pushOutbox(this.db, this.http, await this.deviceId());
      await pullChanges(this.db, this.http);
      this.setState({ status: 'idle', lastSyncAt: new Date().toISOString(), lastError: null });
      if (rejected.length > 0) for (const listener of this.rejectionListeners) listener(rejected);
      void this.files.downloadMissing().catch(() => undefined);
    } catch (error) {
      if (error instanceof NetworkError) {
        this.setState({ status: 'offline', lastError: error.message });
      } else if (error instanceof ApiError && error.status === 401) {
        this.paused = true;
        this.setState({ status: 'unauthorized', lastError: error.message });
      } else {
        this.setState({ status: 'error', lastError: error instanceof Error ? error.message : String(error) });
      }
    }
  }

  /** Sync now (or right after the sync in progress). Never throws. */
  syncNow = (): Promise<void> => {
    if (this.running) {
      this.again = true;
      return this.running;
    }
    const exclusive = navigator.locks ? navigator.locks.request('kit-sync', () => this.run()) : this.run();
    this.running = exclusive.finally(() => {
      this.running = null;
      if (this.again) {
        this.again = false;
        void this.syncNow();
      }
    });
    return this.running;
  };

  /** Debounced sync, after a local change. */
  soon(): void {
    clearTimeout(this.debounce);
    this.debounce = setTimeout(() => void this.syncNow(), DEBOUNCE_MS);
  }

  /** After signing in again: lift the pause a 401 put on syncing. */
  resume(): void {
    this.paused = false;
    void this.syncNow();
  }

  start(): () => void {
    if (this.stopLoop) return this.stopLoop;
    const onOnline = () => void this.syncNow();
    const onOffline = () => this.setState({ status: 'offline' });
    const onVisible = () => {
      if (document.visibilityState === 'visible') void this.syncNow();
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisible);
    const timer = setInterval(onVisible, INTERVAL_MS);
    void this.syncNow();
    this.stopLoop = () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(timer);
      this.stopLoop = null;
    };
    return this.stopLoop;
  }

  reset(): void {
    globalThis.localStorage?.removeItem(LAST_SYNC_KEY);
    this.paused = false;
    this.setState({ status: 'signed_out', lastSyncAt: null, lastError: null });
  }
}
